/* P.N.E. V6.1 | projected-gap 3D media ring + expanding series rail.
 * Standalone components: no third-party libraries and no checkout mutations.
 */
(() => {
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const all = (s, root = document) => Array.from(root.querySelectorAll(s));
  const live = new WeakSet();

  function createRing(root) {
    if (live.has(root)) return;
    live.add(root);
    const viewport = root.querySelector('[data-pne-ring-viewport]');
    const cards = all('[data-pne-ring-card]', root);
    if (!viewport || cards.length < 2) return;
    const previous = root.querySelector('[data-pne-ring-prev]');
    const next = root.querySelector('[data-pne-ring-next]');
    const n = cards.length;
    let pos = 0, velocity = 0, dragging = false, dragged = false, moved = 0;
    let lastX = 0, pointerId = null, lastT = 0, idleUntil = 0;
    let visible = true, raf = 0, previousFrame = 0;
    let values = null, cardWidth = 210;
    const desktop = () => matchMedia('(min-width: 990px)').matches;
    const shouldSpin = root.dataset.autoSpin !== 'false' && !reduce();
    // One carousel-card advance in roughly 1.7–3.2s, rather than 4–10s.
    // Keep autoplay running while hovering; only dragging or wheel input interrupts.
    const spinSpeed = clamp(Number(root.dataset.autoSpeed || 25), 10, 60) / 42000; // cards/ms
    const stopFor = (ms = 800) => { idleUntil = performance.now() + ms; };

    // A cylinder viewed from its interior side: the centre is furthest away,
    // and the two ends curve toward the viewer. Angle spacing is solved using
    // apparent (perspective-projected) card edges, NOT constant 3D angles.
    function measure() {
      const vw = viewport.clientWidth;
      cardWidth = Number.parseFloat(getComputedStyle(cards[0]).width) || (desktop() ? 240 : 180);
      const perspective = desktop() ? Math.max(1150, vw * 1.28) : Math.max(780, vw * 2.25);
      const radius = desktop() ? Math.max(600, vw * .67) : Math.max(400, vw * 1.28);
      const zbase = desktop() ? 180 : 120;
      const gap = desktop() ? clamp(vw * .012, 12, 25) : 9;
      viewport.style.setProperty('--pne-ring-perspective', `${perspective}px`);
      const geometry = t => {
        const z = radius * (1 - Math.cos(t)) - zbase;
        const k = perspective / Math.max(100, perspective - z);
        return { x: radius * Math.sin(t) * k, z, k,
          half: .5 * cardWidth * Math.cos(t * .48) * k };
      };
      const angles = [0];
      let previousRight = geometry(0).half;
      for (let i = 1; i <= 9; i++) {
        let low = angles[i - 1], high = 1.44;
        for (let j = 0; j < 35; j++) {
          const mid = (low + high) / 2;
          const g = geometry(mid);
          if (g.x - g.half >= previousRight + gap) high = mid;
          else low = mid;
        }
        const angle = high;
        angles.push(angle);
        const g = geometry(angle);
        previousRight = g.x + g.half;
      }
      values = { angles, radius, zbase, perspective, vw };
      draw();
    }
    function getAngle(distance) {
      const a = values.angles;
      const integer = clamp(Math.floor(distance), 0, a.length - 2);
      const fraction = clamp(distance - integer, 0, 1);
      return a[integer] + (a[integer + 1] - a[integer]) * fraction;
    }
    function draw() {
      if (!values) return;
      const { radius, zbase, vw } = values;
      for (let i = 0; i < n; i++) {
        let delta = i - pos;
        delta = ((delta + n * 10 + n / 2) % n + n) % n - n / 2;
        const absolute = Math.abs(delta);
        const card = cards[i];
        if (absolute > 5.1) { card.classList.add('is-ring-hidden'); continue; }
        card.classList.remove('is-ring-hidden');
        const sign = Math.sign(delta);
        const angle = getAngle(absolute) * sign;
        const x = radius * Math.sin(angle);
        const z = radius * (1 - Math.cos(angle)) - zbase;
        const tilt = angle * 180 / Math.PI * .48;
        const centre = absolute < .42;
        card.classList.toggle('is-centered', centre);
        card.style.transform = `translate(-50%,-50%) translate3d(${x.toFixed(2)}px,0,${z.toFixed(2)}px) rotateY(${tilt.toFixed(2)}deg)`;
        card.style.zIndex = String(Math.round(1000 + z));
        const projectedX = x * values.perspective / (values.perspective - z);
        const opacity = clamp((vw * .54 - Math.abs(projectedX)) / Math.max(60, vw * .18), .06, 1);
        card.style.opacity = opacity.toFixed(3);
        card.style.pointerEvents = opacity < .20 ? 'none' : 'auto';
        card.setAttribute('aria-hidden', opacity < .2 ? 'true' : 'false');
        card.tabIndex = opacity < .2 ? -1 : 0;
      }
    }
    function animate(now) {
      raf = 0;
      const dt = previousFrame ? clamp(now - previousFrame, 0, 50) : 16;
      previousFrame = now;
      if (!visible) return;
      if (!dragging && !reduce()) {
        if (Math.abs(velocity) > .00002) {
          pos += velocity * dt;
          velocity *= Math.pow(.91, dt / 16);
         } else if (shouldSpin && now > idleUntil) {
          pos += dt * spinSpeed;
        }
      }
      // A bounded local coordinate avoids precision loss during long sessions.
      if (pos > n * 5 || pos < -n * 5) pos = ((pos % n) + n) % n;
      draw();
      if (!reduce() && visible) raf = requestAnimationFrame(animate);
    }
    function start() { if (!raf && !reduce() && visible) { previousFrame = 0; raf = requestAnimationFrame(animate); } else draw(); }
    const step = (dir) => { pos += dir; velocity = 0; stopFor(1600); draw(); start(); };
    previous?.addEventListener('click', () => step(-1));
    next?.addEventListener('click', () => step(1));
    viewport.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); step(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
    viewport.addEventListener('pointerdown', event => {
      if (event.button !== undefined && event.button !== 0) return;
      dragging = true; dragged = false; moved = 0;
      pointerId = event.pointerId; lastX = event.clientX; lastT = performance.now();
      velocity = 0; stopFor(500);
    });
    viewport.addEventListener('pointermove', event => {
      if (!dragging || event.pointerId !== pointerId) return;
      const dx = event.clientX - lastX;
      moved += Math.abs(dx);
      lastX = event.clientX;
      if (moved > 6) dragged = true;
      const deltaT = Math.max(12, performance.now() - lastT);
      lastT = performance.now();
      const delta = -dx / Math.max(80, cardWidth * .86);
      pos += delta;
      velocity = clamp(delta / deltaT, -.012, .012);
      if (dragged) viewport.classList.add('is-grabbing');
      draw();
    });
    const end = () => {
      if (!dragging) return;
      dragging = false; pointerId = null;
      viewport.classList.remove('is-grabbing');
      if (!dragged) velocity = 0;
      if (dragged) setTimeout(() => { dragged = false; }, 180);
      start();
    };
    viewport.addEventListener('pointerup', end);
    viewport.addEventListener('pointercancel', end);
    viewport.addEventListener('lostpointercapture', end);
    viewport.addEventListener('click', e => {
      if (dragged) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    viewport.addEventListener('dragstart', e => e.preventDefault());
    // Pointer hover alone never freezes the self-running gallery.
    // Wheel / trackpad over the gallery nudges the ring without scrolling the page away.
    viewport.addEventListener('wheel', event => {
      if (Math.abs(event.deltaX) < 3 && !event.shiftKey) return;
      event.preventDefault();
      pos += clamp((event.deltaX || event.deltaY) / Math.max(280, cardWidth * 2), -.65, .65);
      velocity = 0; stopFor(700); draw(); start();
    }, { passive:false });
    viewport.addEventListener('focusin', () => stopFor(900));
    const io = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0]?.isIntersecting ?? true;
      if (visible) start(); else { cancelAnimationFrame(raf); raf = 0; }
    }, { threshold: .05 }) : null;
    io?.observe(root);
    const ro = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    ro?.observe(viewport);
    measure(); start();
    root.addEventListener('shopify:section:unload', () => { ro?.disconnect(); io?.disconnect(); cancelAnimationFrame(raf); });
  }

  function createExpanded(root) {
    if (live.has(root)) return;
    live.add(root);
    const rail = root.querySelector('[data-pne-expand-rail]');
    if (!rail) return;
    const cards = all('[data-pne-expand-card]', rail);
    if (cards.length < 2) return;
    let active = cards.findIndex(c => c.classList.contains('is-active'));
    if (active < 0) active = 0;
    const mobile = () => matchMedia('(max-width: 989px)').matches;
    function measure() {
      if (mobile()) return;
      const width = rail.clientWidth;
      const collapsed = clamp(window.innerWidth * .07, 64, 112);
      const open = Math.max(280, width - (cards.length - 1) * (collapsed + 10));
      rail.style.setProperty('--pne-expand-open-width', `${open}px`);
      rail.style.setProperty('--pne-expand-caption-width', `${Math.max(200, Math.min(open - 48, 620))}px`);
    }
    function activate(i) {
      active = i;
      cards.forEach((card, idx) => card.classList.toggle('is-active', idx === i));
      measure();
    }
    cards.forEach((card, i) => {
      card.addEventListener('mouseenter', () => { if (!mobile()) activate(i); });
      card.addEventListener('focus', () => activate(i));
      card.addEventListener('click', event => {
        // Purchase and Details are distinct controls; do not cancel their action.
        if (event.target.closest('button, .pne-expanded-card__purchase-actions')) return;
        if (mobile() && active !== i) { event.preventDefault(); activate(i); }
      });
    });
    rail.addEventListener('mouseleave', () => { if (!mobile()) activate(0); });
    addEventListener('resize', measure, { passive: true });
    measure(); activate(active);
  }

  function boot(root = document) {
    all('[data-pne-ring]', root).forEach(createRing);
    all('[data-pne-expanded-series="true"]', root).forEach(createExpanded);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
